import type { IntlService } from 'ember-intl';

import type RouterService from '@ember/routing/router-service';
import { classify as EmberClassify } from '@ember/string';
import { isPresent } from '@ember/utils';

import type RouteInfo from '@ember/routing/route-info';
import type { SafeString } from '@ember/template';
import type { ContentValue } from '@glint/template';
import Config from 'volta/config/environment';

export type TText = string | null | undefined | SafeString | ContentValue;

/**
 * This function transforms a given string by making the characters upper case
 * @param {string} text The text to transform
 * @return The uppercase string if the string exists otherwise null
 */
export function upper(text?: string): string | null {
  return isPresent(text) ? `${text}`.toUpperCase() : null;
}

export function lower(text?: string): string | null {
  return isPresent(text) ? `${text}`.toLowerCase() : null;
}

export function abbreviateNumber(
  num: number | null,
  fixed?: number | null,
  formatFn?: (d: number) => string
) {
  if (num === null) {
    return null;
  } // terminate early
  if (num === 0) {
    return '0';
  } // terminate early
  fixed = !fixed || fixed < 0 ? 0 : fixed; // number of decimal places to show
  const b = num.toPrecision(2).split('e'); // get power
  const k = b[1] ? Math.floor(Math.min(+b[1].slice(1), 14) / 3) : 0; // floor at decimals, ceiling at trillions
  const c = k < 1 ? num.toFixed(0 + fixed) : (num / Math.pow(10, k * 3)).toFixed(1 + fixed); // divide by power
  const d = +c < 0 ? +c : Math.abs(+c); // enforce -0 is 0
  return (typeof formatFn === 'function' ? formatFn(d) : d) + (['', 'K', 'M', 'B', 'T'][k] ?? ''); // append power
}

export const makeNumberFormatter = (
  format: 'shortScale' | 'percentage' | undefined,
  intl: IntlService
) => {
  const options: Parameters<typeof intl.formatNumber>[1] | undefined =
    format === 'shortScale'
      ? {
          notation: 'compact',
          compactDisplay: 'short'
        }
      : format === 'percentage'
        ? { style: 'percent' }
        : undefined;
  return (value: number) => intl.formatNumber(value, options);
};

export function classify(word: string) {
  return EmberClassify(word);
}

/**
 * Javascript implementation of Java's String.hashCode() method
 * Source : https://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 * @param str input string
 * @returns Hash code 32bit integer
 */
export function hashCode(str: string): number {
  let hash: number = 0;
  let chr: number;
  if (str.length === 0) {
    return hash;
  }
  for (let i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    /* tslint:disable:no-bitwise */
    hash = (hash << 5) - hash + chr;
    hash = Math.trunc(hash); // Convert to 32bit integer
    /* tslint:enable:no-bitwise */
  }
  return hash;
}

/**
 * Removes every accent of a word
 */
export function strNormalizeAccent(str: string) {
  return str.normalize('NFD').replaceAll(/\p{Diacritic}/gu, '');
}

export const sanitized = (str?: string) => strNormalizeAccent(str ?? '').toLowerCase();

export const getCurrencySymbol = (locale: string, currency: string) =>
  (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
    .replaceAll(/\d/g, '')
    .trim();

export function userName(firstName?: string, lastName?: string, login?: string) {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return login;
}

export const tryParseRouteInfo = (url: URL, router: RouterService) => {
  try {
    const routeInfo = router.recognize(url.pathname + url.search);
    return routeInfo?.name === 'not-found' ? undefined : (routeInfo as RouteInfo);
  } catch {
    return undefined;
  }
};

export const tryParseURL = (url?: string | null) => {
  if (!url) {
    return undefined;
  }
  try {
    const urlObject = url.startsWith('/') ? new URL(url, Config.api.host) : new URL(url);
    return urlObject.origin === 'null' ? undefined : urlObject;
  } catch {
    return undefined;
  }
};

// https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
export const decodeBase64 = (base64: string): string => {
  const binString = atob(base64);
  // @ts-expect-error
  const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0));
  return new TextDecoder().decode(bytes);
};

export const splitToLines = (text: string) => {
  return text.split(/[\n\r]+/);
};

export const replaceInTemplate = (
  template: string,
  replacements: Record<string, string | null | undefined>
) => {
  return template.replace(/{([^{}]+)}/g, (_, key) => {
    return replacements[key] ?? '';
  });
};
